<template>
  <v-menu
    bottom
    left
    offset-y
    :close-on-content-click="false"
    v-model="opened"
    eager
  >
    <template v-slot:activator="{ props }">
      <v-btn stacked v-bind="props" class="top-nav-icon">
        <v-badge
          v-if="unreadAlertCount"
          color="red"
          :content="unreadAlertCountDisplay"
          overlap
        >
          <v-icon large>mdi-bell</v-icon>
        </v-badge>

        <v-icon large v-else>mdi-bell</v-icon>
      </v-btn>
    </template>

    <AlertsDropDown
      :unreadAlertCount="unreadAlertCount"
      :alerts="alerts"
      :loaded="alertsLoaded"
      @selectAlert="selectAlert"
      @refresh="refresh"
    />
  </v-menu>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import gql from 'graphql-tag';
import { isString } from '@metools/tcheck';

import AlertsDropDown from './AlertsDropDown.vue';
import { alertsApolloClient } from '../../helpers/api';
import { useRootStore } from '../../stores/root';
import { fetchAlerts } from './alert_api';

const opened = ref(false);
const unreadAlertCount = ref();
/** @type {import('vue').Ref<undefined | null | number>} */
const refreshInterval = ref(null);
const unreadAlertCountDisplay = computed(() => {
  if (unreadAlertCount.value > 99) {
    return '99+';
  }
  return unreadAlertCount.value;
});

const store = useRootStore();
const { userId } = storeToRefs(store);

async function getUnreadAlertCount() {
  const response = await alertsApolloClient.query({
    query: gql`
      query guac{
        getNumberOfUnreadNotificationsByUser(userId: "${userId?.value}")
      }
    `,
    fetchPolicy: 'no-cache',
  });
  unreadAlertCount.value =
    response?.data?.getNumberOfUnreadNotificationsByUser || 0;
}

onMounted(() => {
  startAutoRefresh();
});

// fetch all alerts
/** @type {import('vue').Ref<import('./alert_api').AlertObject[]>} */
const alerts = ref([]);
const alertsLoaded = ref(false);

async function fetchAlertsFromAPI() {
  if (!isString(userId?.value)) {
    return;
  }

  try {
    const resp = await fetchAlerts(userId.value);
    alerts.value = resp;

    alertsLoaded.value = true;
  } catch (e) {
    console.error('Error fetching alerts:', e);
  }
}

watch(opened, async (value) => {
  if (value && !alertsLoaded.value) {
    await fetchAlertsFromAPI();
  }
});

watch(userId, () => {
  alertsLoaded.value = false;
  // fetchAlerts();
  getUnreadAlertCount();
});

function refresh() {
  alertsLoaded.value = false;
  fetchAlertsFromAPI();
  getUnreadAlertCount();
}

function startAutoRefresh() {
  stopAutoRefresh();
  getUnreadAlertCount();
  refreshInterval.value = window.setInterval(() => {
    getUnreadAlertCount();
    if (opened.value && alertsLoaded.value) {
      fetchAlertsFromAPI();
    }
  }, 30000);
}

function stopAutoRefresh() {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
    refreshInterval.value = null;
  }
}

/**
 * @param {import('./alert_api').AlertObject} alert
 */
async function selectAlert(alert) {
  console.log('Alert selected:', alert);

  if (!alert.isRead) {
    alert.isRead = true;
    unreadAlertCount.value -= 1;
    alertsApolloClient.mutate({
      mutation: gql`
        mutation mnr{
          markNotificationsAsRead(userId: "${userId.value}", notificationIds: ["${alert.timestamp}"])
        }
      `,
    });
  }

  try {
    const urlRaw = JSON.parse(alert.payload)?.url;

    if (!urlRaw) {
      throw new Error('No URL found');
    }

    const urlObj = new URL(urlRaw);

    const url = urlObj.hostname.includes('qsrsoft.com')
      ? `${urlObj.pathname}?${urlObj.searchParams.toString()}`
      : urlRaw;

    window.location.href = url;
  } catch (e) {
    console.error(e);
  }
}

onUnmounted(() => {
  stopAutoRefresh();
});
</script>
